import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, isToday, isYesterday } from 'date-fns';
import { ChevronRight, Plus, Pencil, Trash2, MessageSquareQuote } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { useRelativeDate } from '../../hooks';
import { Version, VersionChange } from './hooks/useVersionHistory';
import { cn } from '../../../lib/utils';

interface VersionTimelineProps {
  versions: Version[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean;
  onViewDetail: (version: Version) => void;
  onLoadMore: () => void;
}

interface GroupedVersions {
  label: string;
  versions: Version[];
}

function groupVersionsByDay(versions: Version[], t: (key: string) => string): GroupedVersions[] {
  const groups = new Map<string, Version[]>();

  versions.forEach((version) => {
    const date = new Date(version.changed_at);
    let label: string;

    if (isToday(date)) {
      label = t('versioning.today');
    } else if (isYesterday(date)) {
      label = t('versioning.yesterday');
    } else {
      label = format(date, 'MMMM d, yyyy');
    }

    const existing = groups.get(label) || [];
    existing.push(version);
    groups.set(label, existing);
  });

  return Array.from(groups.entries()).map(([label, vers]) => ({
    label,
    versions: vers,
  }));
}

/** Format a date string (ISO or date-only) for display. */
function formatDateValue(value: string): string {
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    // Date-only (no time component) - use short format
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(date, 'MMM d, yyyy');
    }
    return format(date, 'PPp');
  } catch {
    return value;
  }
}

/** Build a human-readable description for a single change entry. */
function describeChange(change: VersionChange, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const { field, old_value, new_value, type } = change;

  // Format date values for column types that are dates
  const isDateType = change.column_type === 'date' || change.column_type === 'datetime';
  const oldDisplay = isDateType && old_value ? formatDateValue(old_value) : old_value;
  const newDisplay = isDateType && new_value ? formatDateValue(new_value) : new_value;

  if (type === 'list') {
    const parts: string[] = [];
    if (change.added?.length) {
      parts.push(t('versioning.changes.addedTo', { items: change.added.join(', '), field }));
    }
    if (change.removed?.length) {
      parts.push(t('versioning.changes.removedFrom', { items: change.removed.join(', '), field }));
    }
    return parts.join('; ');
  }

  if (type === 'checklist') {
    if (change.action === 'completed') return t('versioning.changes.completed', { value: new_value });
    if (change.action === 'uncompleted') return t('versioning.changes.uncompleted', { value: new_value });
    if (change.action === 'added') return t('versioning.changes.addedChecklistItem', { value: new_value });
    if (change.action === 'removed') return t('versioning.changes.removedChecklistItem', { value: old_value });
    return t('versioning.changes.changedChecklist');
  }

  // Field or column_value changes
  if (!oldDisplay && newDisplay) {
    return t('versioning.changes.setFieldTo', { field, value: newDisplay });
  }
  if (oldDisplay && !newDisplay) {
    return t('versioning.changes.clearedField', { field });
  }
  if (oldDisplay && newDisplay) {
    return t('versioning.changes.changedFieldFromTo', { field, oldValue: oldDisplay, newValue: newDisplay });
  }
  return t('versioning.changes.changedField', { field });
}

/** Format a snake_case field name to a human-readable label (fallback). */
function formatFieldName(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\bid\b/g, 'ID')
    .replace(/\bids\b/g, 'IDs')
    .replace(/^./, (s) => s.toUpperCase());
}

/** Build a short human-readable summary of what changed (legacy fallback). */
function buildChangeSummaryLegacy(version: Version, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const fields = version.changed_fields;
  if (!fields || fields.length === 0) return '';

  const excluded = new Set(['id', 'path', 'depth', 'position', 'board_id', 'tenant_id']);
  const meaningful = fields.filter((f) => !excluded.has(f));

  if (meaningful.length === 0) {
    return fields.map(formatFieldName).join(', ');
  }

  if (meaningful.length <= 3) {
    return meaningful.map(formatFieldName).join(', ');
  }

  return (
    meaningful.slice(0, 2).map(formatFieldName).join(', ') +
    ` ${t('app.nMore', { count: meaningful.length - 2 })}`
  );
}

const changeTypeConfig = {
  created: {
    icon: Plus,
    colorClass: 'text-success',
    dotClass: 'bg-success',
    bgClass: 'bg-success/10',
  },
  updated: {
    icon: Pencil,
    colorClass: 'text-primary',
    dotClass: 'bg-primary',
    bgClass: 'bg-primary/10',
  },
  deleted: {
    icon: Trash2,
    colorClass: 'text-destructive',
    dotClass: 'bg-destructive',
    bgClass: 'bg-destructive/10',
  },
} as const;

export function VersionTimeline({
  versions,
  isLoading,
  error,
  hasMore,
  onViewDetail,
  onLoadMore,
}: VersionTimelineProps) {
  const { t } = useTranslation('common');

  const groupedVersions = useMemo(() => groupVersionsByDay(versions, t), [versions, t]);

  if (isLoading && versions.length === 0) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-destructive py-8">
        {t('common:app.error')}: {error.message}
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        {t('versioning.noHistory')}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {groupedVersions.map((group) => (
        <div key={group.label} className="space-y-1">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 pb-1">
            {group.label}
          </div>
          <div className="space-y-1">
            {group.versions.map((version) => (
              <VersionTimelineItem
                key={version.id}
                version={version}
                onViewDetail={() => onViewDetail(version)}
              />
            ))}
          </div>
        </div>
      ))}

      {hasMore && (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? t('common:app.loading') : t('versioning.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}

interface VersionTimelineItemProps {
  version: Version;
  onViewDetail: () => void;
}

/** Max change descriptions to show inline in the timeline. */
const MAX_INLINE_CHANGES = 3;

function VersionTimelineItem({ version, onViewDetail }: VersionTimelineItemProps) {
  const { t } = useTranslation('common');
  const formatRelative = useRelativeDate();
  const config = changeTypeConfig[version.change_type];
  const Icon = config.icon;

  const timeAgo = formatRelative(new Date(version.changed_at));
  const fullTime = format(new Date(version.changed_at), 'PPp');

  const userName = version.changed_by_name || t('versioning.unknownUser');

  // A revision is a boundary in the document's life, not a property of the edit —
  // so it takes over the event slot (the numeral replaces the change-type icon)
  // and names itself in place of the verb, instead of hanging a second badge off
  // an "updated" row. The `info` hue is unused by the created/updated/deleted
  // triad, so a re-issue reads as its own kind of event at a glance.
  //
  // `!= null` (not `!== null`) on purpose: version payloads persist in the
  // offline React Query cache for a day, so a page loaded right after this
  // shipped can hand us a cached version with the field absent entirely —
  // `undefined` must read as "no revision", not render "Revision undefined".
  const hasRevision = version.revision_number != null;
  const revision = version.revision_number;

  // Use rich changes from backend if available, else fall back to field names
  const changes = version.changes;
  const hasRichChanges = changes && changes.length > 0;
  const legacySummary = !hasRichChanges && version.change_type !== 'created'
    ? buildChangeSummaryLegacy(version, t)
    : null;

  return (
    <button
      type="button"
      className={cn(
        'w-full text-left px-3 py-2.5 rounded-md transition-colors',
        'hover:bg-muted/60 group cursor-pointer'
      )}
      onClick={onViewDetail}
    >
      <div className="flex items-start gap-3">
        {/* Event marker: the revision numeral when this version re-issued the
            document, otherwise the change-type icon. Same slot, same footprint. */}
        {hasRevision ? (
          <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-info/10 text-[11px] font-semibold leading-none tabular-nums text-info">
            {revision}
          </div>
        ) : (
          <div className={cn('mt-0.5 rounded-full p-1.5', config.bgClass)}>
            <Icon className={cn('h-3 w-3', config.colorClass)} />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Who + action */}
          <div className="flex items-baseline gap-1.5 text-sm">
            <span className="font-medium truncate">{userName}</span>
            <span
              className={cn(
                'text-xs font-medium',
                hasRevision ? 'text-info' : config.colorClass
              )}
            >
              {hasRevision
                ? t('versioning.revision', { number: revision })
                : t(`versioning.${version.change_type}`).toLowerCase()}
            </span>
          </div>

          {/* Change reason — a deliberate, user-explained "why" (e.g. a
              post-approval amendment or a quote revision). Shown prominently
              above the field-diffs: it is the headline of the change, not a
              detail. Benefits every versioned entity that records a reason. */}
          {version.change_reason && (
            <div className="mt-1 flex items-start gap-1.5 rounded-md border-s-2 border-primary bg-primary/5 px-2 py-1">
              <MessageSquareQuote className="h-3 w-3 shrink-0 mt-0.5 text-primary" />
              <p className="text-xs text-foreground break-words line-clamp-3">
                {version.change_reason}
              </p>
            </div>
          )}

          {/* Rich change descriptions */}
          {hasRichChanges && (
            <div className="mt-0.5 space-y-0.5">
              {changes.slice(0, MAX_INLINE_CHANGES).map((change, i) => (
                <p key={i} className="text-xs text-muted-foreground truncate">
                  {describeChange(change, t)}
                </p>
              ))}
              {changes.length > MAX_INLINE_CHANGES && (
                <p className="text-xs text-muted-foreground/70">
                  {t('versioning.moreChanges', { count: changes.length - MAX_INLINE_CHANGES })}
                </p>
              )}
            </div>
          )}

          {/* Legacy summary fallback */}
          {legacySummary && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {legacySummary}
            </p>
          )}

          {/* When */}
          <p className="text-xs text-muted-foreground/70 mt-0.5" title={fullTime}>
            {timeAgo}
          </p>
        </div>

        {/* Chevron */}
        <ChevronRight className="h-4 w-4 text-muted-foreground/40 mt-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 rtl:rotate-180" />
      </div>
    </button>
  );
}
